import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import IdsMonthViewAttributeMixin from '../ids-month-view/ids-month-view-attribute-mixin';
import IdsDateAttributeMixin from '../../mixins/ids-date-attribute-mixin/ids-date-attribute-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsPickerPopup from '../ids-popup/ids-picker-popup';

import {
  subtractDate, isValidDate, hoursTo24, removeDateRange, gregorianToUmalqura
} from '../../utils/ids-date-utils/ids-date-utils';
import { stringToBool, stringToNumber } from '../../utils/ids-string-utils/ids-string-utils';
import {
  IdsDatePickerCommonAttributes,
} from './ids-date-picker-common';

// Components
import './ids-month-year-picklist';
import '../ids-button/ids-button';
import '../ids-modal/ids-modal-button';
import '../ids-expandable-area/ids-expandable-area';
import '../ids-month-view/ids-month-view';
import '../ids-time-picker/ids-time-picker-popup';
import '../ids-toggle-button/ids-toggle-button';
import '../ids-toolbar/ids-toolbar';
import '../ids-toolbar/ids-toolbar-section';
// Types
import type IdsElement from '../../core/ids-element';
import type IdsButton from '../ids-button/ids-button';
import type IdsExpandableArea from '../ids-expandable-area/ids-expandable-area';
import type IdsLocale from '../ids-locale/ids-locale';
import type IdsModalButton from '../ids-modal/ids-modal-button';
import type IdsMonthView from '../ids-month-view/ids-month-view';
import type IdsMonthYearPicklist from './ids-month-year-picklist';

import type {
  IdsRangeSettings,
  IdsRangeSettingsInterface,
  IdsLegendSettings,
  IdsDisableSettings
} from '../ids-month-view/ids-month-view-common';

import type { IdsDayselectedEvent } from '../ids-month-view/ids-month-view';
import type IdsTimePickerPopup from '../ids-time-picker/ids-time-picker-popup';
import type IdsToggleButton from '../ids-toggle-button/ids-toggle-button';
import type IdsToolbar from '../ids-toolbar/ids-toolbar';
import type IdsToolbarSection from '../ids-toolbar/ids-toolbar-section';
import type IdsDatePicker from './ids-date-picker';

import styles from './ids-date-picker-popup.scss';
import IdsText from '../ids-text/ids-text';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import IdsButtonGroup from '../ids-button/ids-button-group';

type IdsDatePickerPopupButton = IdsToggleButton | IdsModalButton | IdsButton;

const Base = IdsMonthViewAttributeMixin(
  IdsDateAttributeMixin(
    IdsLocaleMixin(
      IdsPickerPopup
    )
  )
);

/**
 * IDS Date Picker Popup Component
 * @type {IdsDatePickerPopup}
 * @inherits IdsPickerPopup
 * @mixes IdsDateAttributeMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsMonthViewAttributeMixin
 */
@customElement('ids-date-picker-popup')
@scss(styles)
class IdsDatePickerPopup extends Base implements IdsRangeSettingsInterface {
  constructor() {
    super();
  }

  protected expandableArea: IdsExpandableArea | null | undefined;

  protected monthView: IdsMonthView | null | undefined;

  monthYearPicklist: IdsMonthYearPicklist | null | undefined;

  protected timepicker: IdsTimePickerPopup | null | undefined;

  protected toolbar: IdsToolbar | null | undefined;

  protected buttonSet: IdsButtonGroup | null | undefined;

  triggerFieldInput?: HTMLElement;

  private currentlySelectedPicklist = '';

  #isProcessingTodayBtn = false;

  connectedCallback() {
    super.connectedCallback();
    this.configureComponents();
    this.attachEventListeners();
    this.attachExpandedListener();
    this.configurePopup();
  }

  /**
   * Detaches all references and cleans up component connections.
   * Removes event listeners, clears component references, and resets internal state.
   * This method is typically called during component cleanup or when the popup needs
   * to be completely disconnected from its target and related components.
   * @returns {void}
   */
  detachReference() {
    this.triggerFieldInput = undefined;
    this.expandableArea = null;
    this.monthView = null;
    this.monthYearPicklist = null;
    this.timepicker = null;
    this.toolbar = null;
    this.buttonSet = null;
    this.targetElem = undefined;
    this.target = null;
    this.originalParentElement = undefined;
    this.attachmentParentElement = undefined;
    (this.onTargetChange as any) = null;

    if (this.popup) {
      this.popup?.setAttribute?.(attributes.ARROW_TARGET, ``);
      this.popup?.removeOpenEvents?.(true);
      this.popup?.detachReference?.();
      this.popup.alignTarget = undefined;
      this.popup.popupOpenEventsTarget = null;
      (this.popup.onOutsideClick as any) = null;
      this.popup.arrowTarget = null;
      (this.popup.onPlaceEnd as any) = null;
      (this.popup.onPlace as any) = null;
      this.expandableArea = null;
      this.monthView = null;
      this.monthYearPicklist = null;
      this.popup.alignTarget = null;
      this.popup.container = null;
    }

    this.triggerElem = null;
    this.target = null;
    this.state = {};
    this.container = null;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.removeEventListeners();
    this.expandableArea = null;
    this.monthView = null;
    this.monthYearPicklist = null;
    this.timepicker = null;
    this.toolbar = null;
    this.buttonSet = null;
  }

  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      ...IdsDatePickerCommonAttributes,
      attributes.EXPANDED,
      attributes.POSITION_STYLE,
      attributes.SHOW_TODAY,
      attributes.SHOW_APPLY,
      attributes.SHOW_CANCEL,
      attributes.SHOW_CLEAR
    ];
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    const isBtnGroupVisible = this.showCancel
      || this.showClear
      || this.showApply
      || (this.useRange || this.expanded)
      || this.showToday;

    const todayBtn = this.showToday ? `<ids-button appearance="tertiary" css-class="no-padding" class="btn-today">
      <ids-text
        class="btn-today-text"
        font-size="16"
        translate-text="true"
        font-weight="semi-bold"
      >Today</ids-text>
    </ids-button>` : '';

    return `<ids-popup class="ids-date-picker-popup" type="menu" align="bottom, left" arrow="bottom" tabIndex="-1" animated position-style="fixed">
      <slot slot="content" name="toolbar">
        ${this.toolbarTemplate()}
      </slot>
      <div class="ids-date-picker-content" slot="content">
        <ids-expandable-area
          expand-style="fill"
          expanded="${this.expanded}"
          class="ids-date-picker-expandable"
        >
          <ids-month-year-picklist
            slot="pane"></ids-month-year-picklist>
        </ids-expandable-area>
        <ids-month-view
          compact="true"
          is-date-picker="true"
          show-week-numbers="${this.showWeekNumbers}"
          show-today=${this.showToday}
          ${this.showApply ? `show-apply=${this.showApply}` : ''}
          first-day-of-week="${this.firstDayOfWeek}"
          year="${this.year}"
          month="${this.month}"
          day="${this.day}"
          use-range="${this.useRange}"
        ></ids-month-view>
      ${this.timepickerTemplate()}
      </div>
      <ids-button-group slot="content" class="popup-footer${isBtnGroupVisible ? '' : ' hidden'}" part="footer" ${isBtnGroupVisible ? '' : ' hidden'} compact="true">
        <ids-button-group-section gap="12" class="popup-btn-group">
          ${todayBtn}
          <ids-button appearance="secondary" class="popup-btn popup-btn-cancel" cancel ${this.showCancel ? '' : ' hidden'}>
            <span>Cancel</span>
          </ids-button>
          <ids-button appearance="secondary" class="popup-btn popup-btn-clear" part="btn-clear" ${this.showClear ? '' : ' hidden'}>
            <span>Clear</span>
          </ids-button>
          <ids-button appearance="primary" class="popup-btn popup-btn-apply"${this.useRange || this.showApply || this.expanded ? ' disabled' : ' hidden'} part="btn-apply">
            <span>Apply</span>
          </ids-button>
        </ids-button-group-section>
      </ids-button-group>
    </ids-popup>`;
  }

  /**
   * @returns {string} containing the inner Timepicker's template
   */
  private timepickerTemplate(): string {
    if (!this.hasTime()) {
      return '';
    }
    return `
      <ids-time-picker-popup
        ${!this.hasTime() ? 'hidden' : ''}
        minute-interval="${this.minuteInterval}"
        second-interval="${this.secondInterval}"
        use-current-time="${this.useCurrentTime}"
        embeddable="true"
        autoupdate="true"
        format="${this.format}"
        value="${this.value}"
      ></ids-time-picker-popup>
    `;
  }

  /**
   * @returns {string} containing the inner Toolbar's template
   */
  private toolbarTemplate(): string {
    const [month, year] = this.formatMonthText().split(' ');
    const prevBtn = `<ids-button class="btn-previous">
      <ids-text audible="true" translate-text="true">PreviousMonth</ids-text>
      <ids-icon icon="chevron-left"></ids-icon>
    </ids-button>`;

    const nextBtn = `<ids-button class="btn-next">
      <ids-text audible="true" translate-text="true">NextMonth</ids-text>
      <ids-icon icon="chevron-right"></ids-icon>
    </ids-button>`;

    return `<ids-toolbar class="month-view-header" tabbable="true">
      <ids-toolbar-section class="toolbar-buttonset monthview-nav-prev" align="start" flex-grow="1" type="fluid"${this.expanded ? ' inactive' : ''}>
        ${prevBtn}
      </ids-toolbar-section>

      <ids-toolbar-section favor align="center">
        <div class="month-year-container">
          <ids-hyperlink class="month dropdown-text-trigger" font-size="20" clickable tabIndex="0" aria-live="polite">${month}</ids-hyperlink>
          <ids-hyperlink class="year dropdown-text-trigger" font-size="20" clickable tabIndex="0" aria-live="polite">${year}</ids-hyperlink>
        </div>
      </ids-toolbar-section>

      <ids-toolbar-section class="toolbar-buttonset monthview-nav-next" align="end" type="fluid"${this.expanded ? ' inactive' : ''}>
        ${nextBtn}
      </ids-toolbar-section>
    </ids-toolbar>`;
  }

  /**
   * @returns {Array<string>} Date Picker vetoable events
   */
  vetoableEventTypes: Array<string> = ['beforeshow'];

  /**
   * @readonly
   * @returns {NodeList<IdsDatePickerPopupButton>} containing all buttons in the Date Picker Popup
   */
  get buttons() {
    return this.container?.querySelectorAll<IdsDatePickerPopupButton>('ids-button, ids-modal-button, ids-toggle-button');
  }

  /**
   * show-today attribute
   * @returns {boolean} showToday param converted to boolean from attribute value
   */
  get showToday(): boolean {
    const attrVal = this.getAttribute(attributes.SHOW_TODAY);

    // true by default if no attribute
    return attrVal !== null ? stringToBool(attrVal) : true;
  }

  /**
   * Set whether or not month view today button should be show
   * @param {string|boolean|null} val show-today attribute value
   */
  set showToday(val: string | boolean | null) {
    const doShow = stringToBool(val);
    if (doShow) {
      this.setAttribute(attributes.SHOW_TODAY, 'true');
      this.todayBtnEl?.removeAttribute(attributes.HIDDEN);
    } else {
      this.setAttribute(attributes.SHOW_TODAY, 'false');
      this.todayBtnEl?.setAttribute(attributes.HIDDEN, 'true');
    }
    this.updateButtonGroupVisibility();
  }

  /**
   * Handle changes to the first day of week setting
   * @param {number} newValue - The new first day of week value (0-6, where 0 is Sunday)
   */
  onFirstDayOfWeekChange(newValue: number) {
    this.monthView?.setAttribute(attributes.FIRST_DAY_OF_WEEK, `${newValue}`);
    this.monthYearPicklist?.setAttribute(attributes.FIRST_DAY_OF_WEEK, `${newValue}`);
  }

  onMonthChange(newValue: number, isValid: boolean) {
    if (isValid) {
      this.monthView?.setAttribute(attributes.MONTH, `${newValue}`);
      this.monthYearPicklist?.setAttribute(attributes.MONTH, `${newValue}`);
    } else {
      this.monthView?.removeAttribute(attributes.MONTH);
      this.monthYearPicklist?.removeAttribute(attributes.MONTH);
    }
  }

  onYearChange(newValue: number, isValid: boolean) {
    if (isValid) {
      this.monthView?.setAttribute(attributes.YEAR, `${newValue}`);
      this.monthYearPicklist?.setAttribute(attributes.MONTH, `${newValue}`);
    } else {
      this.monthView?.removeAttribute(attributes.YEAR);
      this.monthYearPicklist?.removeAttribute(attributes.YEAR);
    }
  }

  onDayChange(newValue: number, isValid: boolean) {
    if (isValid) {
      this.monthView?.setAttribute(attributes.DAY, `${newValue}`);
      this.monthYearPicklist?.setAttribute(attributes.DAY, `${newValue}`);
    } else {
      this.monthView?.removeAttribute(attributes.DAY);
      this.monthYearPicklist?.removeAttribute(attributes.DAY);
    }
  }

  onFormatChange() {
    this.configureComponents();
    if (this.monthView) this.monthView.setAttribute('format', this.format);

    if (this.timepicker) {
      this.timepicker.setAttribute('format', this.format);

      if (this.hasTime()) {
        this.timepicker.setAttribute('value', this.value);
        this.timepicker.removeAttribute(attributes.HIDDEN);
      } else {
        this.timepicker.setAttribute(attributes.HIDDEN, '');
      }
    }
    this.updateTimepickerDisplay();
  }

  /**
   * @param {IdsLocale} locale the new locale object
   */
  onLocaleChange = (locale: IdsLocale | undefined) => {
    this.updateMonthYearPickerTriggerDisplay(locale);
    if (this.monthYearPicklist) {
      this.monthYearPicklist.locale = this.locale;
      this.monthYearPicklist.language = this.language.name;
    }
    if (this.monthView) {
      this.monthView.locale = this.locale;
      this.monthView.language = this.language.name;
    }
    this.shadowRoot?.querySelectorAll('[translate-text]').forEach((textElem: Element) => {
      (textElem as IdsText).language = this.language.name;
    });
  };

  get popupBtnGroupEl(): IdsButtonGroup | null | undefined {
    return this.buttonSet?.querySelector<IdsButtonGroup>('.popup-btn-group');
  }

  get applyBtnEl(): IdsModalButton | null | undefined {
    return this.container?.querySelector<IdsModalButton>('.popup-btn-apply');
  }

  get cancelBtnEl(): IdsModalButton | null | undefined {
    return this.container?.querySelector<IdsModalButton>('.popup-btn-cancel');
  }

  get todayBtnEl(): IdsButton | null | undefined {
    return this.container?.querySelector<IdsButton>('.btn-today');
  }

  /**
   * expanded attribute
   * @returns {boolean} whether the month/year picker expanded or not
   */
  get expanded(): boolean {
    return stringToBool(this.getAttribute(attributes.EXPANDED));
  }

  /**
   * Set whether or not the month/year picker should be expanded
   * @param {string|boolean|null} val expanded attribute value
   */
  set expanded(val: string | boolean | null) {
    const boolVal = stringToBool(val);
    if (boolVal) {
      this.setAttribute(attributes.EXPANDED, `${boolVal}`);
      this.expandableArea?.setAttribute(attributes.EXPANDED, `${boolVal}`);
      this.toolbar?.querySelector<IdsToolbarSection>('.monthview-nav-prev')?.setAttribute(attributes.INACTIVE, 'true');
      this.toolbar?.querySelector<IdsToolbarSection>('.monthview-nav-next')?.setAttribute(attributes.INACTIVE, 'true');
    } else {
      this.removeAttribute(attributes.EXPANDED);
      this.expandableArea?.removeAttribute(attributes.EXPANDED);
      this.toolbar?.querySelector<IdsToolbarSection>('.monthview-nav-prev')?.removeAttribute(attributes.INACTIVE);
      this.toolbar?.querySelector<IdsToolbarSection>('.monthview-nav-next')?.removeAttribute(attributes.INACTIVE);
    }
  }

  onDisableSettingsChange(val: IdsDisableSettings) {
    if (this.monthView) this.monthView.disableSettings = val;
  }

  onLegendSettingsChange(val: IdsLegendSettings) {
    if (this.monthView) this.monthView.legend = val;
  }

  /**
   * Defer to the inner IdsMonthView for getting `rangeSettings` if possible
   * @returns {IdsRangeSettings} month view range settings
   */
  getRangeSettings(): IdsRangeSettings {
    return this.monthView?.rangeSettings || this.rangeSettings;
  }

  /**
   * Defer to the inner IdsMonthView for storing `rangeSettings
   * @param {IdsRangeSettings} val incoming range settings
   */
  setRangeSettings(val: IdsRangeSettings) {
    this.monthView!.rangeSettings = val;
  }

  /**
   * Defer to the inner IdsMonthView for storing useRange
   * @param {boolean} val  range setting
   */
  setUseRangeSettings(val: boolean) {
    this.monthView!.useRange = val;
  }

  /**
   * Helper method to format range values based on showSingleDateAsRange setting
   * @param {string} formattedStart - The formatted start date
   * @param {string} formattedEnd - The formatted end date
   * @param {string} separator - The range separator
   * @returns {string} The formatted range value
   * @private
   */
  #formatRangeValue(formattedStart: string, formattedEnd: string, separator: string): string {
    const datePicker = getClosest(this.target, 'ids-date-picker') as IdsDatePicker;
    const showSingleDateAsRange = datePicker?.rangeSettings.collapseSameDates;

    if (showSingleDateAsRange && formattedStart === formattedEnd) {
      return formattedStart;
    }
    return `${formattedStart}${separator}${formattedEnd}`;
  }

  onRangeSettingsChange(val: IdsRangeSettings) {
    if (this.monthView) {
      const btnApply = this.applyBtnEl;
      this.setRangeSettings(val);

      if (val?.start && val?.end) {
        const formattedStart = this.localeAPI.formatDate(this.setTime(val.start), { pattern: this.format });
        const formattedEnd = this.localeAPI.formatDate(this.setTime(val.end), { pattern: this.format });

        this.value = this.#formatRangeValue(formattedStart, formattedEnd, val.separator);
        if (!val.selectWeek) btnApply?.removeAttribute(attributes.DISABLED);
      } else {
        if (val?.separator && this.value.indexOf(val?.separator) > -1) {
          this.value = removeDateRange(this.value, val?.separator);
        }
        if (!this.showApply) btnApply?.setAttribute(attributes.DISABLED, 'true');
      }

      if (val?.selectWeek) {
        btnApply?.setAttribute(attributes.HIDDEN, 'true');
      }
    }
  }

  /**
   * Minute Interval attribute
   * @returns {number} minuteInterval value
   */
  get minuteInterval(): number {
    return stringToNumber(this.getAttribute(attributes.MINUTE_INTERVAL));
  }

  /**
   * Set interval in minutes dropdown
   * @param {string|number|null} val minute-interval attribute value
   */
  set minuteInterval(val: string | number | null) {
    const numberVal = stringToNumber(val);

    if (numberVal) {
      this.setAttribute(attributes.MINUTE_INTERVAL, `${numberVal}`);
      this.timepicker?.setAttribute(attributes.MINUTE_INTERVAL, `${numberVal}`);
    } else {
      this.removeAttribute(attributes.MINUTE_INTERVAL);
      this.timepicker?.removeAttribute(attributes.MINUTE_INTERVAL);
    }
  }

  /**
   * second-interval attribute
   * @returns {number} secondInterval value
   */
  get secondInterval(): number {
    return stringToNumber(this.getAttribute(attributes.SECOND_INTERVAL));
  }

  /**
   * Set interval in seconds dropdown
   * @param {string|number|null} val second-interval attribute value
   */
  set secondInterval(val: string | number | null) {
    const numberVal = stringToNumber(val);

    if (numberVal) {
      this.setAttribute(attributes.SECOND_INTERVAL, `${numberVal}`);
      this.timepicker?.setAttribute(attributes.SECOND_INTERVAL, `${numberVal}`);
    } else {
      this.removeAttribute(attributes.SECOND_INTERVAL);
      this.timepicker?.removeAttribute(attributes.SECOND_INTERVAL);
    }
  }

  /**
   * show-clear attribute
   * @returns {boolean} showClear param converted to boolean from attribute value
   */
  get showClear(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_CLEAR));
  }

  /**
   * Set whether or not to show clear button in the calendar popup
   * @param {string|boolean|null} val show-clear attribute value
   */
  set showClear(val: string | boolean | null) {
    const boolVal = stringToBool(val);
    const btn = this.container?.querySelector('.popup-btn-clear');

    if (boolVal) {
      this.setAttribute(attributes.SHOW_CLEAR, boolVal.toString());
      btn?.removeAttribute(attributes.HIDDEN);
    } else {
      this.removeAttribute(attributes.SHOW_CLEAR);
      btn?.setAttribute(attributes.HIDDEN, (!boolVal).toString());
    }
    this.updateButtonGroupVisibility();
  }

  /**
   * show-cancel attribute
   * @returns {boolean} showCancel param converted to boolean from attribute value
   */
  get showCancel(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_CANCEL));
  }

  /**
   * Set whether or not to show cancel button when the picker is expanded
   * @param {string|boolean|null} val show-cancel attribute value
   */
  set showCancel(val: string | boolean | null) {
    const boolVal = stringToBool(val);
    const btn = this.container?.querySelector('.popup-btn-cancel');

    if (boolVal) {
      this.setAttribute(attributes.SHOW_CANCEL, String(boolVal));
      btn?.removeAttribute(attributes.HIDDEN);
    } else {
      this.removeAttribute(attributes.SHOW_CANCEL);
      btn?.setAttribute(attributes.HIDDEN, (!boolVal).toString());
    }
    this.updateButtonGroupVisibility();
  }

  /**
   * show-apply attribute
   * @returns {boolean} showCancel param converted to boolean from attribute value
   */
  get showApply(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_APPLY));
  }

  /**
   * Set whether or not to show cancel button when the picker is expanded
   * @param {string|boolean|null} val show-cancel attribute value
   */
  set showApply(val: string | boolean | null) {
    const boolVal = stringToBool(val);
    const btn = this.container?.querySelector('.popup-btn-apply');

    if (boolVal) {
      this.setAttribute(attributes.SHOW_APPLY, String(boolVal));
      btn?.removeAttribute(attributes.HIDDEN);
      if (this.monthView) this.monthView.showApply = true;
    } else {
      this.removeAttribute(attributes.SHOW_APPLY);
      btn?.setAttribute(attributes.HIDDEN, (!boolVal).toString());
      if (this.monthView) this.monthView.showApply = false;
    }
    this.updateButtonGroupVisibility();
  }

  /**
   * show-picklist-year attribute, default is true
   * @returns {boolean} showPicklistYear param converted to boolean from attribute value
   */
  get showPicklistYear(): boolean {
    const attrVal = this.getAttribute(attributes.SHOW_PICKLIST_YEAR);

    if (attrVal) {
      return stringToBool(attrVal);
    }

    return true;
  }

  /**
   * Whether or not to show a list of years in the picklist
   * @param {string | boolean | null} val value to be set as show-picklist-year attribute converted to boolean
   */
  set showPicklistYear(val: string | boolean | null) {
    const boolVal = stringToBool(val);

    this.setAttribute(attributes.SHOW_PICKLIST_YEAR, String(boolVal));
    this.monthYearPicklist?.setAttribute(attributes.SHOW_PICKLIST_YEAR, String(boolVal));
  }

  /**
   * show-picklist-month attribute, default is true
   * @returns {boolean} showPicklistMonth param converted to boolean from attribute value
   */
  get showPicklistMonth(): boolean {
    const attrVal = this.getAttribute(attributes.SHOW_PICKLIST_MONTH);

    if (attrVal) {
      return stringToBool(attrVal);
    }

    return true;
  }

  /**
   * Whether or not to show a list of months in the picklist
   * @param {string | boolean | null} val value to be set as show-picklist-month attribute converted to boolean
   */
  set showPicklistMonth(val: string | boolean | null) {
    const boolVal = stringToBool(val);

    this.setAttribute(attributes.SHOW_PICKLIST_MONTH, String(boolVal));
    this.monthYearPicklist?.setAttribute(attributes.SHOW_PICKLIST_MONTH, String(boolVal));
  }

  /**
   * show-picklist-week attribute
   * @returns {boolean} showPicklistWeek param converted to boolean from attribute value
   */
  get showPicklistWeek(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_PICKLIST_WEEK));
  }

  /**
   * Whether or not to show week numbers in the picklist
   * @param {string | boolean | null} val value to be set as show-picklist-week attribute converted to boolean
   */
  set showPicklistWeek(val: string | boolean | null) {
    const boolVal = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.SHOW_PICKLIST_WEEK, String(boolVal));
      this.monthYearPicklist?.setAttribute(attributes.SHOW_PICKLIST_WEEK, String(boolVal));
    } else {
      this.removeAttribute(attributes.SHOW_PICKLIST_WEEK);
      this.monthYearPicklist?.removeAttribute(attributes.SHOW_PICKLIST_WEEK);
    }
  }

  /**
   * use-current-time attribute
   * @returns {number} useCurrentTime param converted to boolean from attribute value
   */
  get useCurrentTime(): boolean {
    return stringToBool(this.getAttribute(attributes.USE_CURRENT_TIME));
  }

  /**
   * Set whether or not to show current time in the time picker
   * @param {string|boolean|null} val useCurrentTime param value
   */
  set useCurrentTime(val: string | boolean | null) {
    const boolVal = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.USE_CURRENT_TIME, String(boolVal));
      this.timepicker?.setAttribute(attributes.USE_CURRENT_TIME, String(boolVal));
    } else {
      this.removeAttribute(attributes.USE_CURRENT_TIME);
      this.timepicker?.removeAttribute(attributes.USE_CURRENT_TIME);
    }
  }

  onUseRangeChange(val: boolean) {
    const btnApply = this.applyBtnEl;
    if (val) {
      this.monthView?.setAttribute(attributes.USE_RANGE, String(val));
      btnApply?.removeAttribute(attributes.HIDDEN);

      if (this.monthView) {
        if (this.monthView.rangeSettings.start === undefined && this.monthView.rangeSettings.end === undefined) {
          btnApply?.setAttribute(attributes.DISABLED, 'true');
        }
      } else {
        btnApply?.setAttribute(attributes.DISABLED, 'true');
      }
    } else {
      this.monthView?.removeAttribute(attributes.USE_RANGE);
      if (!this.expanded && !this.showApply) btnApply?.setAttribute(attributes.HIDDEN, 'true');
      btnApply?.removeAttribute(attributes.DISABLED);
    }
  }

  /**
   * value attribute
   * @returns {string} value param
   */
  get value(): string {
    return this.getAttribute(attributes.VALUE) || '';
  }

  /**
   * Set Date Picker Popup's stored value. Should parse a date from the value.
   * @param {string|null} val value param
   */
  set value(val: string | null) {
    if (!val) {
      this.dateValue = null;
      this.removeAttribute(attributes.VALUE);
      return;
    }

    let newDate = this.getDateValue(val);

    if (this.useRange) {
      const rangeSettings = this.getRangeSettings();
      const separator = rangeSettings.separator;
      const [start] = val.split(separator);
      newDate = this.getDateValue(start.trim());
    }

    const setDateAttributes = () => {
      this.setAttribute(attributes.VALUE, val);
      this.timepicker?.setAttribute(attributes.VALUE, val);
    };

    if (newDate && isValidDate(newDate)) {
      this.dateValue = newDate;

      // must set first before the sync
      setDateAttributes();
      this.syncDateAttributes(newDate);
    } else {
      setDateAttributes();
    }
  }

  private onPicklistExpand() {
    const btnApply = this.applyBtnEl;
    const btnCancel = this.cancelBtnEl;

    this.monthYearPicklist?.activatePicklist();
    this.buttonSet?.removeAttribute(attributes.HIDDEN);
    this.popupBtnGroupEl?.removeAttribute(attributes.HIDDEN);
    this.popupBtnGroupEl?.classList.remove('hidden');
    this.buttonSet?.classList.remove('hidden');

    if (this.todayBtnEl) {
      this.todayBtnEl.classList.add('hidden');
    }

    btnApply?.removeAttribute(attributes.HIDDEN);
    btnApply?.removeAttribute(attributes.DISABLED);

    if (this.showCancel) {
      btnCancel?.removeAttribute(attributes.HIDDEN);
    }

    // Temporarily remove tab index to selected day when picklist expands
    if (this.monthView && typeof this.monthView.setSelectDayTabIndex === 'function') {
      this.monthView.setSelectDayTabIndex('-1');
    }

    // temporariy fix need to call the place function twice
    this.popup?.place(true);
    this.popup?.place(true);
  }

  private onPicklistCollapse() {
    if (this.monthYearPicklist?.deactivatePicklist) this.monthYearPicklist?.deactivatePicklist();
    this.updateActionButtonStateOnShow();

    // arrange the currently focus element after picklist has collapsed
    if (this.monthView && typeof this.monthView.setSelectDayTabIndex === 'function') {
      this.monthView.setSelectDayTabIndex('0');
      this.shadowRoot?.querySelector<HTMLElement>(`.dropdown-text-trigger.${this.currentlySelectedPicklist}`)?.focus();
    }

    // temporariy fix need to call the place function twice
    this.popup?.place(true);
    this.popup?.place(true);
  }

  /**
   * Checks internal component refs based on settings
   */
  configureComponents() {
    // ensure trigger field is set as target when popup is a datagrid filter
    const popupTriggerField = this?.parentElement?.querySelector<IdsElement>('ids-trigger-field');
    if (!this.target && this.getAttribute('data-filter-type') === 'date' && popupTriggerField) {
      this.target = popupTriggerField;
    }

    this.expandableArea = this.container?.querySelector<IdsExpandableArea>('ids-expandable-area');
    this.monthView = this.container?.querySelector<IdsMonthView>('ids-month-view');
    this.monthYearPicklist = this.container?.querySelector<IdsMonthYearPicklist>('ids-month-year-picklist');

    const firstDayOfTheWeek = this.getAttribute(attributes.FIRST_DAY_OF_WEEK);
    if (firstDayOfTheWeek !== null && this.monthView) {
      this.monthView.setAttribute(attributes.FIRST_DAY_OF_WEEK, firstDayOfTheWeek);
    }

    if (this.hasTime()) {
      this.timepicker = this.container?.querySelector<IdsTimePickerPopup>('ids-time-picker-popup');
      if (this.timepicker && this.useUtc) this.timepicker.useUtc = this.useUtc;
      if (this.useCurrentTime) this.setCurrentTime();
      // Initialize time picker with default values if no value is set
      if (!this.value && this.timepicker) {
        this.timepicker.syncTimeAttributes(null);
      }
    } else {
      this.timepicker?.remove();
      this.timepicker = null;
    }

    this.toolbar = this.container?.querySelector<IdsToolbar>('ids-toolbar');
    this.buttonSet = this.container?.querySelector<IdsButtonGroup>('ids-button-group');
  }

  /**
   * Defers `activeDate` to the inner MonthView's `activeDate` attribute,
   * otherwise fallback to the one generated by this one's date props
   * @returns {Date} activeDate from the inner monthView component
   */
  getActiveDate() {
    return this.monthView?.activeDate || this.activeDate;
  }

  /**
   * Expanded/Collapsed event for Month/Year Picklist
   */
  private attachExpandedListener() {
    if (this.expandableArea) {
      this.offEvent('afterexpand');
      this.onEvent('afterexpand', this.expandableArea, () => {
        this.onPicklistExpand();
      });

      this.offEvent('beforecollapse');
      this.onEvent('beforecollapse', this.expandableArea, () => {
        this.onPicklistCollapse();
      });
    }
  }

  /**
   * Expands the month/year picker based on the trigger element clicked
   * @private
   * @param {HTMLElement} triggerElement - The element that triggered the expansion
   * @returns {boolean} True if the picker was expanded, false otherwise
   */
  private expandMonthYearPicker(triggerElement: HTMLElement) {
    const targetClassList = Array.from(triggerElement.classList);
    const isDropdownTriggerText = targetClassList.includes('dropdown-text-trigger');
    if (isDropdownTriggerText) {
      const textBtn: IdsText | null = (triggerElement as IdsText);
      if (textBtn && !textBtn.disabled) {
        this.currentlySelectedPicklist = '';
        const isYear = targetClassList.includes('year');
        const isMonth = targetClassList.includes('month');

        this.#syncPicklistDate();

        if (isYear) {
          this.monthYearPicklist?.setPickList(false, true, false);
          this.currentlySelectedPicklist = 'year';
        } else if (isMonth) {
          this.monthYearPicklist?.setPickList(true, false, false);
          this.currentlySelectedPicklist = 'month';
        }
        this.expanded = !this.expanded;
      }
      return true;
    }
    return false;
  }

  private attachEventListeners() {
    // Selects day from the monthView (after user input)
    this.offEvent('dayselected.date-picker-calendar');
    this.onEvent('dayselected.date-picker-calendar', this.monthView, (e: CustomEvent) => {
      if (this.showApply && e.detail.isClicked) {
        return;
      }

      this.handleDaySelectedEvent(e);
      e.stopPropagation();
    });

    this.offEvent('datechange');
    this.onEvent('datechange', this.monthView, (e: CustomEvent) => {
      e.stopPropagation();
      this.updateMonthYearPickerTriggerDisplay();
    });

    // Handles input from header buttons
    this.offEvent('click.date-picker-header');
    this.offEvent('keydown.date-picker-header');
    if (this.toolbar) {
      this.onEvent('keydown.date-picker-header', this.toolbar, (e: KeyboardEvent) => {
        if (['Enter', 'Space'].includes(e.key)) {
          this.expandMonthYearPicker(e.target as HTMLElement);
        }
      });

      this.onEvent('click.date-picker-header', this.toolbar, (e: Event) => {
        const target = e.target as HTMLElement;

        if (this.expandMonthYearPicker(e.target as HTMLElement)) {
          e.stopPropagation();
          return;
        }

        const navBtn: IdsButton | null = target.closest('.btn-previous, .btn-next');
        if (navBtn) {
          e.stopPropagation();
          if (!navBtn.disabled) {
            if (navBtn.classList.contains('btn-next')) {
              this.monthView?.changeDate('next-month');
            } else if (navBtn.classList.contains('btn-previous')) {
              this.monthView?.changeDate('previous-month');
            }
          }
        }
      });
    }

    // Handles input from footer buttons
    this.offEvent('click.date-picker-footer');
    this.onEvent('click.date-picker-footer', this.container?.querySelector('.popup-footer'), async (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      e.stopPropagation();

      if (target.closest('.btn-today')) {
        this.handleTodayEvent();
        return;
      }

      if (target.closest('.popup-btn-apply')) {
        this.handleApplyEvent(e);
        return;
      }

      if (target.closest('.popup-btn-clear')) {
        this.clear();
        await this.hide(true);
        return;
      }

      if (target.closest('.popup-btn-cancel')) {
        this.expanded = false;
        await this.hide(true);
      }
    });

    this.offEvent('keydown.date-picker-popup');
    this.onEvent('keydown.date-picker-popup', this.container, async (e: KeyboardEvent) => {
      if (this.expanded || e.key !== 'Tab') {
        return;
      }

      const focusableElements = this.container?.querySelectorAll(
        'ids-button:not([hidden])'
      );
      const currentActiveElement = this.shadowRoot?.activeElement || document.activeElement;

      if (focusableElements && focusableElements[focusableElements.length - 1] === currentActiveElement) {
        (focusableElements[0] as HTMLElement).focus();
        e.preventDefault();
      }
    });

    // Input value change triggers component value change
    if (this.target) {
      this.offEvent('change.date-picker-input');
      this.onEvent('change.date-picker-input', this.target, (e: any) => {
        this.setAttribute(attributes.VALUE, e.detail.value);
      });
    }

    // Time picker value change triggers input value change
    if (this.hasTime()) {
      this.offEvent('timeselected.date-picker-time');
      this.onEvent('timeselected.date-picker-time', this.timepicker, () => {
        this.value = this.localeAPI.formatDate(
          this.setTime(this.getActiveDate()),
          { pattern: this.format }
        );
        this.triggerSelectedEvent();
      });
    }

    // Window resize handler
    this.offEvent('resize.date-picker-window');
    this.onEvent('resize.date-picker-window', window, () => {
      if (this.visible && this.popup) {
        this.#handleDatePickerPositioning();
      }
    });

    if (this.monthYearPicklist && typeof this.monthYearPicklist.offEvent === 'function') {
      this.monthYearPicklist.offEvent('select.month-year-picklist');
      this.monthYearPicklist.onEvent('select.month-year-picklist', this.monthYearPicklist, () => {
        this.handleApplyEvent();
      });
    }
  }

  private removeEventListeners() {
    this.offEvent('dayselected.date-picker-calendar');
    this.offEvent('datechange');
    this.offEvent('click.date-picker-header');
    this.offEvent('click.date-picker-footer');
    this.offEvent('keydown.date-picker-popup');
    this.offEvent('change.date-picker-input');
    this.offEvent('resize.date-picker-window', window);
    if (this.monthYearPicklist && typeof this.monthYearPicklist.offEvent === 'function') {
      this.monthYearPicklist.offEvent('select.month-year-picklist');
    }
  }

  /**
   * Click to apply button event handler
   * @param {MouseEvent} e click event
   */
  private handleApplyEvent(e?: MouseEvent): void {
    e?.stopPropagation();
    if (this.expanded) {
      this.captureValueFromPicklist();
      this.updateMonthYearPickerTriggerDisplay();
      this.expanded = false;
      return;
    }

    const rangeSettings = this.getRangeSettings();
    if (this.useRange) {
      if (rangeSettings.end || (rangeSettings.start && !rangeSettings.end)) {
        if (rangeSettings.minDays && (rangeSettings.start === rangeSettings.end)) {
          rangeSettings.start = subtractDate(rangeSettings.start, rangeSettings.minDays, 'days');
          this.setRangeSettings(rangeSettings);
        }

        const formattedStart = this.localeAPI.formatDate(this.setTime(rangeSettings.start), { pattern: this.format });
        const formattedEnd = this.localeAPI.formatDate(
          this.setTime(rangeSettings.end ?? this.getActiveDate()),
          { pattern: this.format }
        );

        this.value = this.#formatRangeValue(formattedStart, formattedEnd, rangeSettings.separator);

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.hide(true);
        this.triggerSelectedEvent();
      } else {
        this.value = this.localeAPI.formatDate(
          this.setTime(rangeSettings.start ?? this.getActiveDate()),
          { pattern: this.format }
        );
        rangeSettings.start = this.getActiveDate();
        this.setRangeSettings(rangeSettings);
      }

      return;
    }

    if (this.target) {
      this.value = this.localeAPI.formatDate(
        this.setTime(this.getActiveDate()),
        { pattern: this.format }
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.hide(true);
    this.triggerSelectedEvent();
  }

  /**
   * Clears the contents of the Date Picker Popup and its corresponding target, if applicable
   * @returns {void}
   */
  clear() {
    this.expanded = false;
    this.resetRangeSettings();
    this.value = '';

    // Reset time picker to current time when clearing
    if (this.hasTime() && this.timepicker) {
      this.timepicker.syncTimeAttributes(null);
    }

    this.triggerSelectedEvent();
  }

  /**
   * Selected event handler
   * @param {IdsDayselectedEvent} e event from the calendar day selection
   */
  private handleDaySelectedEvent(e: CustomEvent): void {
    const currentDate = this.dateValue;

    // Clear action
    // Deselect the selected date by clicking to the selected date
    if (currentDate instanceof Date && isValidDate(currentDate) && currentDate.getTime() === e.detail.date.getTime()) {
      this.value = '';
      this.monthView?.selectDay();
      this.triggerSelectedEvent(e);

      return;
    }

    if (this.useRange) {
      const rangeSettings = this.getRangeSettings();
      if (rangeSettings.selectWeek) {
        const fixedDate = this.setTime(e.detail.rangeStart as Date);
        const formattedStart = this.localeAPI.formatDate(fixedDate, { pattern: this.format });
        const formattedEnd = e.detail.rangeEnd
          && this.localeAPI.formatDate(this.setTime(e.detail.rangeEnd), { pattern: this.format });

        this.value = this.#formatRangeValue(formattedStart, formattedEnd, rangeSettings.separator);

        e.detail.date = fixedDate;
        e.detail.value = this.value;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.hide(true);
        this.triggerSelectedEvent(e);

        return;
      }

      e.stopPropagation();
      const btnApply = this.applyBtnEl;
      if (e.detail.rangeStart && e.detail.rangeEnd) {
        btnApply?.removeAttribute(attributes.DISABLED);
      } else {
        btnApply?.setAttribute(attributes.DISABLED, 'true');
      }
    } else {
      const fixedDate = this.setTime(e.detail.date);
      this.value = this.localeAPI.formatDate(
        fixedDate,
        { pattern: this.format }
      );
      this.year = e.detail.date.getFullYear();
      this.month = e.detail.date.getMonth();
      this.day = e.detail.date.getDate();

      e.detail.date = fixedDate;
      e.detail.value = this.value;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.hide(true);
      this.triggerSelectedEvent(e);
    }
  }

  /**
   * Click to Today button event handler
   * @returns {void}
   */
  handleTodayEvent(): void {
    if (!this.monthView) return;
    this.#isProcessingTodayBtn = true;
    this.monthView.changeDate('today');

    // If range is enabled just set the start date to today
    if (this.useRange) {
      const rangeSettings = this.getRangeSettings();
      rangeSettings.start = this.getActiveDate();
      rangeSettings.end = null;
      this.setRangeSettings(rangeSettings);
      this.#isProcessingTodayBtn = false;
      return;
    }

    this.value = this.localeAPI.formatDate(
      this.setTime(this.getActiveDate()),
      { pattern: this.format }
    );
    this.triggerSelectedEvent();
    this.#isProcessingTodayBtn = false;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.hide(true);
  }

  /**
   * Gets the value from the selected items in the Month/Year Picklist
   * and sets them in the Date Picker Popup
   * @returns {void}
   */
  private captureValueFromPicklist() {
    if (!this.monthYearPicklist) return;
    const { month, year } = this.monthYearPicklist;
    this.year = year;
    this.month = month;
    const date = this.setTime(this.activeDate);
    this.value = this.getFormattedDate(date);
  }

  /**
   * Defines if the format has hours/minutes/seconds pattern to show time picker
   * @returns {boolean} whether or not to show time picker
   */
  hasTime(): boolean {
    if (!this.format) return false;
    return this.format.includes('h') || this.format.includes('m') || this.format.includes('s');
  }

  /**
   * Helper to set the date with time from time picker
   * @param {any} val date to add time values
   * @returns {Date} date with time values
   */
  private setTime(val: any): Date {
    const date = isValidDate(val) ? val : new Date(val);
    const timePicker = this.container?.querySelector<IdsTimePickerPopup>('ids-time-picker-popup');

    if (!this.hasTime() || !timePicker) return date;

    const hours: number = timePicker.hours ?? 0;
    const minutes: number = timePicker.minutes ?? 0;
    const seconds: number = timePicker.seconds ?? 0;
    const period: string = timePicker.period ?? '';
    const dayPeriodIndex = this.localeAPI?.calendar().dayPeriods?.indexOf(period);

    date.setHours(hoursTo24(hours, dayPeriodIndex), minutes, seconds);

    return date;
  }

  /**
   * Triggers the same `dayselected` event on the Popup's target element that came from the internal IdsMonthView
   * @param {IdsDayselectedEvent} [e] optional event handler to pass arguments
   * @returns {void}
   */
  private triggerSelectedEvent(e?: IdsDayselectedEvent): void {
    let args: any;
    if (e) args = e;
    else {
      const rangeSettings = this.getRangeSettings();
      args = {
        bubbles: true,
        detail: {
          isFromTodayBtn: this.#isProcessingTodayBtn,
          elem: this,
          date: this.getActiveDate(),
          useRange: this.useRange,
          rangeStart: this.useRange && rangeSettings.start ? new Date(rangeSettings.start as string) : null,
          rangeEnd: this.useRange && rangeSettings.end ? new Date(rangeSettings.end as string) : null,
          value: this.value
        }
      };
    }

    // Trigger on target if available, otherwise trigger on the popup itself
    const eventTarget = this.target || this;
    if (eventTarget) {
      this.triggerEvent('dayselected', eventTarget, args);
      this.triggerEvent('applyclicked', eventTarget, args);
    }
  }

  /**
   * Renders or removes an embedded IdsTimePicker component
   */
  private updateTimepickerDisplay() {
    const hasTime = this.hasTime();
    this.container?.classList.toggle('has-time', hasTime);
    if (hasTime) {
      if (!this.timepicker) {
        this.monthView?.insertAdjacentHTML('afterend', this.timepickerTemplate());
        this.timepicker = this.container?.querySelector('ids-time-picker-popup');
      }
    } else {
      this.timepicker?.remove();
      this.timepicker = null;
    }
  }

  /**
   * Takes a string containing a date, or an actual Date object, and syncs its values
   * to the correct properties provided by IdsDateAttributeMixin
   * @param {string | Date} val incoming date string/object
   */
  public syncDateAttributes(val: string | Date) {
    let usableValue = val;
    const rangeSettings = this.getRangeSettings();

    if (typeof val === 'string' && this.useRange && rangeSettings.separator) {
      usableValue = removeDateRange(val, rangeSettings.separator);
    }

    const date = new Date(usableValue);
    const month = date.getMonth();
    const year = date.getFullYear();
    const day = date.getDate();

    if (this.year !== year) this.year = year;
    if (this.month !== month) this.month = month;
    if (this.day !== day) this.day = day;

    if (this.hasTime()) {
      if (this.timepicker) this.timepicker.value = this.value;
    }

    if (this.monthView) {
      if (typeof this.monthView.selectDayFromValue === 'function') {
        this.monthView.selectDayFromValue(`${usableValue}`);
      }
    }

    this.updateMonthYearPickerTriggerDisplay();
  }

  private setCurrentTime() {
    if (this.timepicker) {
      this.timepicker.value = this.localeAPI.formatDate(new Date(), { pattern: this.format });
    }
  }

  /**
   * Helper to format datepicker text in the toolbar
   * @param {IdsLocale} [locale] an optional, provided IdsLocale object
   * @param {Date} [date] an optional, provided Date object (defaults to `this.getActiveDate()`)
   * @returns {string} locale formatted month year
   */
  private formatMonthText(locale?: IdsLocale, date?: Date): string {
    const targetLocale = locale || this.localeAPI;
    const targetDate = date || this.getActiveDate();

    if (!targetLocale) return '';

    const monthKeys = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = targetLocale?.translate(`MonthWide${monthKeys[targetDate.getMonth()]}`);

    // Display Islamic year if locale is Islamic
    const year = targetLocale?.isIslamic()
      ? gregorianToUmalqura(targetDate).year
      : targetDate.getFullYear();

    return `${month} ${year}`;
  }

  /**
   * Updates the text contents of the Month/Year Picker's trigger button to reflect current state
   * @param {IdsLocale} [locale] incoming IdsLocale object, if applicable
   * @param {Date} [date] an optional, provided Date object (defaults to `this.getActiveDate()`)
   * @returns {void}
   */
  updateMonthYearPickerTriggerDisplay(locale?: IdsLocale, date?: Date) {
    const [month, year] = (this.formatMonthText(locale, date) || '').split(' ');
    const toggleBtnTextYear = this.container?.querySelector<IdsText>('.dropdown-text-trigger.year');
    const toggleBtnTextMonth = this.container?.querySelector<IdsText>('.dropdown-text-trigger.month');

    if (toggleBtnTextMonth) {
      toggleBtnTextMonth.innerText = month;
    } else {
      const toggleBtn = this.container?.querySelector('.month-year-container');
      toggleBtn?.insertAdjacentHTML('beforeend', `<ids-hyperlink class="month dropdown-text-trigger" clickable font-size="20" aria-live="polite">${month}</ids-hyperlink>`);
    }

    if (toggleBtnTextYear) {
      toggleBtnTextYear.innerText = year;
    } else {
      const toggleBtn = this.container?.querySelector('.month-year-container');
      toggleBtn?.insertAdjacentHTML('beforeend', `<ids-hyperlink class="year dropdown-text-trigger" clickable font-size="20" aria-live="polite">${year}</ids-hyperlink>`);
    }
  }

  /**
   * Runs when this picker component hides
   * @returns {void}
   */
  onHide() {
    this.container?.setAttribute(htmlAttributes.TABINDEX, '-1');
    this.expanded = false;
  }

  /**
   * Runs when this picker component shows
   * @returns {void}
   */
  onShow(): void {
    this.attachEventListeners();
    this.updateActionButtonStateOnShow();
    this.container?.removeAttribute(htmlAttributes.TABINDEX);
    // Update time if using current time
    if (this.hasTime() && this.useCurrentTime && !this.value) this.setCurrentTime();
    this.#handleDatePickerPositioning();
    this.updateMonthYearPickerTriggerDisplay();
    this.#syncPicklistDate();
    this.focus();
  }

  /**
   * Override to prevent closing when clicking navigation buttons or month/year dropdowns
   * @param {MouseEvent} e the click event
   * @returns {void}
   */
  onOutsideClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;

    // Check if click is within datepicker popup content
    if (target?.closest?.('.btn-previous, .btn-next, .dropdown-text-trigger, ids-expandable-area, ids-month-year-picklist, .month-year-container')) {
      return;
    }

    super.onOutsideClick(e);
  }

  /**
   * Updates the Popup's Modal button states
   */
  private updateActionButtonStateOnShow() {
    const btnApply = this.applyBtnEl;
    const rangeSettings = this.getRangeSettings();
    if (this.monthView && typeof this.monthView.setSelectDayTabIndex === 'function') {
      this.monthView.setSelectDayTabIndex('0');
    }
    const hasPartialRangeSelected = !(rangeSettings.start && rangeSettings.end);

    if (!this.useRange) {
      if (btnApply && !this.showApply) {
        btnApply.setAttribute(attributes.HIDDEN, 'true');
        btnApply.setAttribute(attributes.DISABLED, `${hasPartialRangeSelected}`);
      }
      if (this.showCancel) {
        const btnCancel = this.cancelBtnEl;
        btnCancel?.removeAttribute(attributes.HIDDEN);
      }
      if (this.showToday && this.todayBtnEl) {
        this.todayBtnEl.classList?.remove('hidden');
      }
    } else if (!rangeSettings.selectWeek) {
      btnApply?.removeAttribute(attributes.HIDDEN);
      btnApply?.setAttribute(attributes.DISABLED, `${hasPartialRangeSelected}`);
    } else {
      btnApply?.setAttribute(attributes.HIDDEN, 'true');
    }
  }

  /**
   * Passes through to IdsMonthView to check if a specified date is disabled (not available for choosing)
   * @param {Date} date the date to check
   * @returns {boolean} wheter or not the date is disabled
   */
  isDisabledByDate(date: Date) {
    return this.monthView?.isDisabledByDate(date);
  }

  /**
   * Passes focus to the inner MonthView component
   */
  focus() {
    if (this.expanded) {
      this.monthYearPicklist?.focus();
    } else {
      this.monthView?.focus();
    }
  }

  configurePopup(setX: boolean = true) {
    const isInActionPanel = getClosest(this, 'ids-action-panel');
    if (this.popup && (this.positionStyle === 'fixed' || isInActionPanel)) {
      const modalParent = getClosest(this.popup, 'ids-action-panel, ids-modal');
      this.popup.setAttribute(attributes.POSITION_STYLE, 'fixed');
      this.triggerFieldInput = (this.parentNode?.querySelector('ids-trigger-field')?.shadowRoot?.querySelector('.field-container') as HTMLDivElement);
      let y = (this.triggerFieldInput?.getBoundingClientRect().bottom || 0) - 1;
      const x = setX ? (this.triggerFieldInput?.getBoundingClientRect().x || 0) : 0;

      this.popup.onPlace = (popupRect: any, domElement: HTMLElement) => {
        if (modalParent) {
          popupRect.x = 'unset';
          domElement.style.marginLeft = 'unset';
          domElement.style.marginRight = 'unset';
          domElement.style.marginTop = 'unset';
          const popupWidth = (this.popup as HTMLElement).offsetWidth;
          const popupHeight = (this.popup as HTMLElement).offsetHeight;

          const triggerRect = this.triggerElem!.getBoundingClientRect();
          const modalRect = modalParent.container?.querySelector('.ids-modal-container')?.getBoundingClientRect() || modalParent.getBoundingClientRect();
          const arrowClassList = this.popup!.arrowEl!.classList[1];
          const scrollTop = modalParent.container?.querySelector('.ids-modal-content')?.scrollTop || 0;
          let targetWidth = (this.popup?.alignTarget as HTMLElement)?.offsetWidth;
          const parentRect = this.parentElement?.getBoundingClientRect();

          // Check if this is a data grid filter
          const isDataGridFilter = this.getAttribute(attributes.DATA_FILTER_TYPE) === 'date';
          if (isDataGridFilter) {
            const spaceRight = modalRect.right - triggerRect.right;
            const spaceLeft = triggerRect.left - modalRect.left;

            if (spaceRight < popupWidth && spaceLeft < popupWidth) {
              popupRect.x = (modalRect.width - popupWidth) / 2;
              popupRect.y = (triggerRect.bottom - modalRect.y) + 3;
            } else if (spaceRight < popupWidth) {
              popupRect.x = (triggerRect.right - modalRect.x) - popupWidth + 1;
              popupRect.y = (triggerRect.bottom - modalRect.y) + 3;
            } else {
              popupRect.x = (triggerRect.x - modalRect.x);
              popupRect.y = (triggerRect.bottom - modalRect.y) + 3;
            }
            return popupRect;
          }

          switch (arrowClassList) {
            case 'left':
              popupRect.x = ((triggerRect.x || 0) - modalRect.x) - popupWidth;
              popupRect.x -= 7;
              break;
            case 'top':
              popupRect.y = ((triggerRect.y || 0) - modalRect.y) - popupHeight;
              popupRect.y -= 7;
              break;
            case 'right':
              popupRect.x = ((triggerRect.right || 0) - modalRect.x);
              popupRect.x += 7;
              break;
            default:
              if (scrollTop && scrollTop > 0) domElement.style.marginTop = `-${17 + scrollTop}px`;
              else domElement.style.marginTop = '-10px';

              // Adjust to wider field sizes and datagrid sizes
              if (this.popup?.alignTarget?.nodeName === 'IDS-TRIGGER-BUTTON') targetWidth = (this.popup?.alignTarget.parentElement as HTMLElement)?.offsetWidth;

              if (targetWidth > popupWidth && !this.localeAPI.isRTL()) {
                domElement.style.marginLeft = `${targetWidth - popupWidth}px`;
              }
              if (targetWidth > popupWidth && this.localeAPI.isRTL()) {
                domElement.style.marginRight = `${targetWidth - popupWidth + 4}px`;
                const arrowLeft = parseInt((domElement as any)?.arrowEl?.style.marginLeft);
                requestAnimationFrame(() => {
                  (domElement as any)!.arrowEl!.style.marginLeft = `${arrowLeft + 6}px`;
                });
              }
              if ((parentRect?.left || 0) + popupWidth > (window.visualViewport?.width || 0)) {
                popupRect.x = (window.visualViewport?.width || 0) - popupWidth;
              } else {
                popupRect.x = 'unset';
              }
              popupRect.y = 'unset';
          }
          return popupRect;
        }
        popupRect.y = y;
        popupRect.x = x;

        // Flip it up
        if (y + Number(this.popup?.offsetHeight)
          > Number(document.body?.scrollHeight)) {
          y = (this.triggerFieldInput?.getBoundingClientRect().top || 0) - Number(this.popup?.offsetHeight) + 1;
          popupRect.y = y;
          this.popup?.container?.classList.add('flipped');
        }
        return popupRect;
      };
    }
  }

  /**
   * Sync month/year/day values to the month-year picklist
   * @private
   */
  #syncPicklistDate(): void {
    if (this.monthYearPicklist) {
      this.monthYearPicklist.month = this.month;
      this.monthYearPicklist.year = this.year;
      this.monthYearPicklist.day = this.day;
    }
  }

  /**
   * Handle viewport positioning for the date picker popup
   * @private
   */
  #handleDatePickerPositioning(): void {
    if (!this.popup || !this.popup.alignTarget) return;

    const contentWrapper = this.popup.wrapper;
    const viewportWidth = window.visualViewport?.width || window.innerWidth;
    const viewportHeight = window.innerHeight;
    const initialPopupRect = this.popup.getBoundingClientRect();
    const targetRect = this.popup.alignTarget.getBoundingClientRect();
    const spaceRight = viewportWidth - targetRect.right;
    const spaceBottom = viewportHeight - targetRect.bottom;
    const spaceTop = targetRect.top;
    const fitsRight = spaceRight >= initialPopupRect.width;
    const fitsBottom = spaceBottom >= initialPopupRect.height;
    const fitsTop = spaceTop >= initialPopupRect.height;

    if (!fitsRight) {
      let spaceValue = spaceBottom;
      let fitValue = fitsBottom;

      if (spaceBottom >= spaceTop) {
        this.popup.setAttribute(attributes.ALIGN, attributes.BOTTOM);
        this.popup.setAttribute(attributes.ARROW, attributes.BOTTOM);
      } else {
        this.popup.setAttribute(attributes.ALIGN, attributes.TOP);
        this.popup.setAttribute(attributes.ARROW, attributes.TOP);
        spaceValue = spaceTop;
        fitValue = fitsTop;
      }

      if (!fitValue && contentWrapper) {
        const maxHeight = Math.max(spaceValue - 20, 200);
        contentWrapper.style.maxHeight = `${maxHeight}px`;
        contentWrapper.style.overflowY = 'auto';
      }
    }
  }

  set positionStyle(val: 'fixed' | 'absolute' | null) {
    this.setAttribute(attributes.POSITION_STYLE, val === 'fixed' ? 'fixed' : 'absolute');
    this.configurePopup(false);
  }

  get positionStyle(): 'fixed' | 'absolute' {
    return this.getAttribute(attributes.POSITION_STYLE) === 'fixed' ? 'fixed' : 'absolute';
  }

  /**
   * Updates button group visibility based on whether any buttons are visible
   * @public
   */
  updateButtonGroupVisibility(): void {
    const isBtnGroupVisible = this.showCancel
      || this.showClear
      || this.showApply
      || (this.useRange || this.expanded)
      || this.showToday;

    if (isBtnGroupVisible) {
      this.buttonSet?.removeAttribute(attributes.HIDDEN);
      this.buttonSet?.classList.remove('hidden');
    } else {
      this.buttonSet?.setAttribute(attributes.HIDDEN, 'true');
      this.buttonSet?.classList.add('hidden');
    }

    // Reposition popup if it's currently visible
    if (this.visible && this.popup) {
      this.popup.place();
    }
  }

  set showWeekNumbers(val: boolean | null) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.SHOW_WEEK_NUMBERS, '');
      this.monthView?.setAttribute(attributes.SHOW_WEEK_NUMBERS, '');
    } else {
      this.removeAttribute(attributes.SHOW_WEEK_NUMBERS);
      this.monthView?.removeAttribute(attributes.SHOW_WEEK_NUMBERS);
    }
    this.monthView?.rerenderMonth();
  }

  get showWeekNumbers(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_WEEK_NUMBERS));
  }

  /**
   * Resets the date picker calendar view to today's date.
   * Used when the input is cleared to ensure the popup shows the current month.
   * @param {boolean} [isSilent] when true, suppresses change/dayselected events during the reset
   * @returns {void}
   */
  resetPopupToToday(isSilent: boolean = false) {
    if (!this.monthView) {
      return;
    }

    this.monthView.changeDate('today', {
      isSilent
    });
  }
}

export default IdsDatePickerPopup;
